/*
 * Ranking for the command palette.
 *
 * Kept out of the component because this is the part that is wrong in ways
 * types cannot catch — the ordering, not the rendering. A palette that puts
 * `staging-api` above `api` when you typed "api" feels broken even though
 * both matched, so the tie-breaks are the whole feature.
 *
 * Substring matching rather than fuzzy subsequence matching, deliberately.
 * Fuzzy matching means typing "api" surfaces `a`-`p`-`i` scattered through
 * `admin-pipeline-runner`, which is noise at the top of a list you are trying
 * to hit Enter on.
 */

/* Lower is better. Named steps rather than magic numbers — a plain object and
   not a `const enum`, which `isolatedModules` rejects. */
const Rank = {
  Exact: 0,
  LabelPrefix: 1,
  WordPrefix: 2,
  LabelContains: 3,
  HintPrefix: 4,
  HintContains: 5,
} as const;

export interface Rankable {
  label: string;
  /** The secondary line — a slug, an engine name, a route. Matched, ranked lower. */
  hint?: string | undefined;
}

/**
 * How well `query` matches, or `null` when it does not match at all.
 *
 * An empty query matches everything at equal rank, which is what lets the
 * palette open showing its full list rather than nothing.
 */
export function scoreCommand(query: string, item: Rankable): number | null {
  const q = query.trim().toLowerCase();
  if (q === "") return Rank.Exact;

  const label = item.label.toLowerCase();
  if (label === q) return Rank.Exact;
  if (label.startsWith(q)) return Rank.LabelPrefix;

  // A word boundary inside the label. "api" should find `staging api` before
  // it finds `rapid`, because that is the word someone means.
  if (label.split(/[\s\-_/.]+/).some((word) => word.startsWith(q))) return Rank.WordPrefix;
  if (label.includes(q)) return Rank.LabelContains;

  const hint = item.hint?.toLowerCase();
  if (hint) {
    if (hint.startsWith(q)) return Rank.HintPrefix;
    if (hint.includes(q)) return Rank.HintContains;
  }

  return null;
}

/**
 * Everything that matches, best first, with the input order preserved inside a
 * rank. That stability is what keeps the list from reshuffling under the
 * cursor as someone types a second character.
 */
export function rankCommands<T extends Rankable>(query: string, items: readonly T[]): T[] {
  const scored: { item: T; score: number; index: number }[] = [];

  items.forEach((item, index) => {
    const score = scoreCommand(query, item);
    if (score !== null) scored.push({ item, score, index });
  });

  scored.sort((a, b) => a.score - b.score || a.index - b.index);
  return scored.map((entry) => entry.item);
}
